import { useEffect, useState } from 'react';
import { listBlocked, unblockPlayer, type BlockedPlayer } from '../../net/friendsClient';
import { Link, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useAccountStore, type LanguageCode, type ReducedMotionPref } from '../../stores/account';
import { useReducedMotion } from '../../components/useReducedMotion';
import { WordMark } from '../../components/WordMark';
import { sfx } from '../../audio/sfx';
import { colors, EDGES } from '../../theme/tokens';

// Tab IDs for the landscape-mobile compact layout. At md+ all cards
// render in a grid; at sm only the active tab's card is visible so
// the route fits 360px tall without inner scroll.
type SettingsTab = 'audio' | 'language' | 'combat' | 'motion' | 'players' | 'help';

// Settings — audio, language, motion, tutorial replay. All preferences are
// local-first (zustand/persist). Audio + i18n not yet wired into runtime;
// values are stored for when those systems land. Reduced-motion is live —
// the route transition + synergy chip pulse both honor it via
// useEffectiveReducedMotion.

export function SettingsPage() {
  const navigate = useNavigate();
  const audioMaster = useAccountStore((s) => s.audioMaster);
  const audioMusic = useAccountStore((s) => s.audioMusic);
  const audioSfx = useAccountStore((s) => s.audioSfx);
  const language = useAccountStore((s) => s.language);
  const reducedMotionPref = useAccountStore((s) => s.reducedMotionPref);
  const autoSkipCombat = useAccountStore((s) => s.autoSkipCombat);
  const setAudioMaster = useAccountStore((s) => s.setAudioMaster);
  const setAudioMusic = useAccountStore((s) => s.setAudioMusic);
  const setAudioSfx = useAccountStore((s) => s.setAudioSfx);
  const setLanguage = useAccountStore((s) => s.setLanguage);
  const setReducedMotionPref = useAccountStore((s) => s.setReducedMotionPref);
  const setAutoSkipCombat = useAccountStore((s) => s.setAutoSkipCombat);
  const replayTutorial = useAccountStore((s) => s.replayTutorial);

  // OS-signaled motion preference — surfaced in the "auto" hint so users
  // know what the OS is asking for when their override is set to auto.
  const osReducedMotion = useReducedMotion();

  // Active tab — only consulted at sm (landscape mobile). At md+ the
  // CSS grid renders every card simultaneously and ignores this state.
  const [tab, setTab] = useState<SettingsTab>('audio');

  function handleReplayTutorial() {
    replayTutorial();
    navigate('/menu');
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-1/4 -top-1/4 h-[50vw] w-[50vw] max-h-[640px] max-w-[640px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,46,126,0.14) 0%, rgba(255,46,126,0) 70%)',
        }}
      />

      <section className="relative mx-auto flex h-full w-full max-w-6xl flex-col gap-xs overflow-hidden px-md py-xs md:gap-lg md:px-lg md:py-md lg:max-w-5xl lg:gap-xl lg:py-xl">
        <header className="flex items-center justify-between">
          <span className="md:hidden">
            <WordMark size="sm" />
          </span>
          <span className="hidden md:inline lg:hidden">
            <WordMark size="md" />
          </span>
          <span className="hidden lg:inline">
            <WordMark size="lg" />
          </span>
          <Link
            to="/menu"
            className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite hover:text-marker md:text-[12px] lg:text-[14px]"
          >
            ← menu
          </Link>
        </header>

        {/* Title — hidden at landscape mobile so the 6 setting cards have
            room to render. /settings eyebrow stays as a wayfinder. */}
        <div className="hidden md:block">
          <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
            /settings
          </p>
          <h1 className="mt-xs font-emo text-[28px] leading-none text-emo md:text-[48px] lg:text-[72px]">
            Tweak it
          </h1>
        </div>

        {/* Tab strip — landscape mobile only. Shows one card at a time so
            the route fits 360px tall. Above md the grid renders all cards
            simultaneously and the tabs hide. */}
        <div className="flex shrink-0 gap-[2px] overflow-x-auto md:hidden">
          <TabButton id="audio" current={tab} label="Audio" onSelect={setTab} />
          <TabButton id="language" current={tab} label="Idioma" onSelect={setTab} />
          <TabButton id="combat" current={tab} label="Combat" onSelect={setTab} />
          <TabButton id="motion" current={tab} label="Motion" onSelect={setTab} />
          <TabButton id="players" current={tab} label="Players" onSelect={setTab} />
          <TabButton id="help" current={tab} label="Help" onSelect={setTab} />
        </div>

        {/* Card grid — at sm each card is gated by `tab`; at md+ the
            grid shows all six. xl drops to 3-col for big desktops. */}
        <div className="flex-1 overflow-y-auto md:overflow-visible md:grid md:grid-cols-2 md:gap-lg xl:grid-cols-3">

        {/* Audio — Master gates both Music and SFX. Music drives the
            looping theme; SFX drives the procedural game sounds. The
            "test" buttons play a representative cue at the current mix. */}
        <TabPane active={tab === 'audio'}>
        <SettingsCard eyebrow="audio" title="Volume">
          <VolumeSlider label="Master" value={audioMaster} onChange={setAudioMaster} />
          <VolumeSlider label="Music" value={audioMusic} onChange={setAudioMusic} accent="hotPink" />
          <VolumeSlider
            label="SFX"
            value={audioSfx}
            onChange={setAudioSfx}
            accent="glowStick"
            onTest={() => sfx.success()}
          />
        </SettingsCard>
        </TabPane>

        {/* Language — i18n not yet wired; values save. UI labeled for clarity. */}
        <TabPane active={tab === 'language'}>
        <SettingsCard eyebrow="language" title="Idioma">
          <div className="flex gap-xs md:gap-sm">
            <LanguageToggle current={language} value="en" label="English" onSelect={setLanguage} />
            <LanguageToggle current={language} value="es" label="Español" onSelect={setLanguage} />
          </div>
          <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
            translation strings populate when the i18n pass ships
          </p>
        </SettingsCard>
        </TabPane>

        {/* Combat playback — auto-skip jumps straight to the outcome
            instead of rendering the canvas animation. Useful for replay
            grinding once the player has seen enough combat. */}
        <TabPane active={tab === 'combat'}>
        <SettingsCard
          eyebrow="combat"
          title="Playback"
          hint="Skip the canvas animation and jump straight to the result."
        >
          <ToggleRow
            label="Auto-skip combat"
            sub={autoSkipCombat ? 'on · jumps to outcome' : 'off · play the canvas'}
            checked={autoSkipCombat}
            onChange={setAutoSkipCombat}
          />
        </SettingsCard>
        </TabPane>

        {/* Reduced motion — LIVE. Override the OS preference if needed. */}
        <TabPane active={tab === 'motion'}>
        <SettingsCard
          eyebrow="accessibility"
          title="Reduced motion"
          hint={`OS currently asking for ${osReducedMotion ? 'reduced motion' : 'full motion'}.`}
        >
          <div className="flex flex-wrap gap-xs md:gap-sm">
            <MotionToggle
              current={reducedMotionPref}
              value="auto"
              label="Auto"
              sub="follow OS"
              onSelect={setReducedMotionPref}
            />
            <MotionToggle
              current={reducedMotionPref}
              value="on"
              label="On"
              sub="always reduce"
              onSelect={setReducedMotionPref}
            />
            <MotionToggle
              current={reducedMotionPref}
              value="off"
              label="Off"
              sub="always full"
              onSelect={setReducedMotionPref}
            />
          </div>
        </SettingsCard>
        </TabPane>

        {/* Blocked players -- the undo for the block button on the
            ladder and friends screens. Google Play's UGC policy wants the
            block to be reversible somewhere findable. */}
        <TabPane active={tab === 'players'}>
        <SettingsCard eyebrow="players" title="Blocked">
          <BlockedList />
        </SettingsCard>
        </TabPane>

        {/* Tutorial replay + about + privacy */}
        <TabPane active={tab === 'help'}>
        <SettingsCard eyebrow="help" title="Tutorial & legal">
          <div className="flex flex-col gap-sm">
            <motion.button
              onClick={handleReplayTutorial}
              whileTap={{ scale: 0.97 }}
              className="inline-flex min-h-[44px] items-center rounded-sm bg-stucco2 px-md font-data text-[12px] uppercase tracking-[0.2em] text-bone hover:bg-stucco3 md:text-[13px]"
            >
              ↻ Replay tutorial
            </motion.button>
            <div className="flex flex-wrap gap-md text-[13px] md:text-[12px]">
              <a
                href={import.meta.env.MODE === 'app'
                  ? 'https://raulcontrerasherrera.com/cve/privacy-policy.html'
                  : `${import.meta.env.BASE_URL}privacy-policy.html`}
                target="_blank"
                rel="noreferrer"
                className="font-data uppercase tracking-[0.2em] text-marker hover:text-bone"
              >
                privacy policy →
              </a>
              <span className="font-data uppercase tracking-[0.2em] text-graphite">
                v2 · serifeus studio
              </span>
            </div>
          </div>
        </SettingsCard>
        </TabPane>
        </div>
      </section>
    </main>
  );
}

// --- subcomponents ---

// At sm, gates a card behind tab selection; at md+ always renders.
function TabPane({ active, children }: { active: boolean; children: React.ReactNode }) {
  return <div className={active ? '' : 'hidden md:block'}>{children}</div>;
}

function TabButton({
  id,
  current,
  label,
  onSelect,
}: {
  id: SettingsTab;
  current: SettingsTab;
  label: string;
  onSelect: (id: SettingsTab) => void;
}) {
  const active = current === id;
  return (
    <button
      onClick={() => onSelect(id)}
      aria-pressed={active}
      className={`shrink-0 rounded-sm border px-sm py-[6px] font-data text-[13px] uppercase tracking-[0.2em] transition-colors ${
        active
          ? 'border-sodium bg-sodium/15 text-sodium'
          : 'border-stucco3 bg-stucco2 text-marker hover:text-bone'
      }`}
    >
      {label}
    </button>
  );
}

interface SettingsCardProps {
  eyebrow: string;
  title: string;
  hint?: string;
  children: React.ReactNode;
}

function SettingsCard({ eyebrow, title, hint, children }: SettingsCardProps) {
  return (
    <section
      className="flex flex-col gap-sm rounded-md border bg-stucco1 p-md md:gap-md md:p-lg lg:p-xl"
      style={{ borderColor: EDGES.soft }}
    >
      <div>
        <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker md:text-[12px]">
          {eyebrow}
        </p>
        <h2 className="mt-xs font-cholo text-[22px] leading-none text-bone md:text-[28px] lg:text-[32px]">
          {title}
        </h2>
      </div>
      <div className="flex flex-col gap-md">{children}</div>
      {hint && (
        <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          {hint}
        </p>
      )}
    </section>
  );
}

interface VolumeSliderProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  /** Color accent for the filled track. Defaults to sodium. */
  accent?: 'sodium' | 'hotPink' | 'glowStick';
  /** Optional "test" button — plays a representative cue at the current mix. */
  onTest?: () => void;
}

function VolumeSlider({ label, value, onChange, accent = 'sodium', onTest }: VolumeSliderProps) {
  const accentHex =
    accent === 'hotPink' ? colors.hotPink : accent === 'glowStick' ? colors.glowStick : colors.sodium;

  return (
    <div className="flex flex-col gap-xs md:flex-row md:items-center md:gap-md">
      <span className="font-data text-[13px] uppercase tracking-[0.2em] text-marker md:w-[64px] md:text-[12px]">
        {label}
      </span>
      <div className="flex flex-1 items-center gap-sm">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-stucco3 outline-none"
          style={{
            background: `linear-gradient(to right, ${accentHex} 0%, ${accentHex} ${value}%, ${colors.stucco3} ${value}%, ${colors.stucco3} 100%)`,
          }}
          aria-label={`${label} volume`}
        />
        <span
          className="w-[40px] shrink-0 text-right font-data text-[12px] font-bold leading-none md:text-[14px]"
          style={{ color: accentHex }}
        >
          {value}
        </span>
        {onTest && (
          <button
            onClick={onTest}
            aria-label={`Test ${label}`}
            className="shrink-0 rounded-sm border border-stucco3 px-sm py-[4px] font-data text-[12px] uppercase tracking-[0.2em] text-graphite transition-colors hover:border-glowStick/60 hover:text-glowStick"
          >
            test
          </button>
        )}
      </div>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  sub: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ label, sub, checked, onChange }: ToggleRowProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      role="switch"
      aria-checked={checked}
      className="flex min-h-[44px] items-center justify-between gap-md rounded-sm border bg-stucco2 px-md py-sm transition-colors hover:bg-stucco3"
      style={{
        borderColor: checked ? '#ff8a3d' : 'rgba(245,236,214,0.08)',
      }}
    >
      <span className="flex flex-col items-start gap-[2px]">
        <span
          className={`font-data text-[12px] uppercase tracking-[0.2em] md:text-[13px] ${
            checked ? 'text-sodium' : 'text-bone'
          }`}
        >
          {label}
        </span>
        <span className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite md:text-[11px]">
          {sub}
        </span>
      </span>
      <span
        className={`inline-flex h-[24px] w-[44px] shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-sodium' : 'bg-stucco3'
        }`}
        aria-hidden
      >
        <span
          className={`inline-block h-[20px] w-[20px] rounded-full bg-bone transition-transform ${
            checked ? 'translate-x-[22px]' : 'translate-x-[2px]'
          }`}
        />
      </span>
    </button>
  );
}

interface LanguageToggleProps {
  current: LanguageCode;
  value: LanguageCode;
  label: string;
  onSelect: (v: LanguageCode) => void;
}

function LanguageToggle({ current, value, label, onSelect }: LanguageToggleProps) {
  const active = current === value;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`min-h-[44px] rounded-sm border px-md font-data text-[12px] uppercase tracking-[0.2em] transition-colors md:text-[13px] ${
        active
          ? 'border-sodium bg-sodium/15 text-sodium'
          : 'border-stucco3 bg-stucco2 text-marker hover:text-bone'
      }`}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

interface MotionToggleProps {
  current: ReducedMotionPref;
  value: ReducedMotionPref;
  label: string;
  sub: string;
  onSelect: (v: ReducedMotionPref) => void;
}

function MotionToggle({ current, value, label, sub, onSelect }: MotionToggleProps) {
  const active = current === value;
  return (
    <button
      onClick={() => onSelect(value)}
      className={`flex min-h-[56px] flex-1 flex-col items-start gap-[2px] rounded-sm border px-md py-sm transition-colors md:min-w-[120px] md:flex-none ${
        active
          ? 'border-sodium bg-sodium/15'
          : 'border-stucco3 bg-stucco2 hover:bg-stucco3'
      }`}
      aria-pressed={active}
    >
      <span
        className={`font-data text-[12px] uppercase tracking-[0.2em] md:text-[13px] ${
          active ? 'text-sodium' : 'text-bone'
        }`}
      >
        {label}
      </span>
      <span className="font-data text-[13px] uppercase tracking-[0.2em] text-graphite md:text-[10px]">
        {sub}
      </span>
    </button>
  );
}

function BlockedList() {
  const [list, setList] = useState<BlockedPlayer[] | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    listBlocked().then((l) => { if (!cancelled) setList(l); });
    return () => { cancelled = true; };
  }, []);

  async function unblock(id: string) {
    const ok = await unblockPlayer(id);
    if (ok) setList((prev) => (prev ? prev.filter((b) => b.id !== id) : prev));
  }

  if (list === undefined) {
    return <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite">loading…</p>;
  }
  if (list === null) {
    return <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite">Could not reach the server.</p>;
  }
  if (list.length === 0) {
    return (
      <p className="font-data text-[12px] leading-relaxed text-graphite">
        Nobody blocked. Use the ⚑ button next to a player on the ladder or in your crew to report or block them.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-xs">
      {list.map((b) => (
        <li
          key={b.id}
          className="flex items-center justify-between gap-md rounded-sm border bg-stucco2 px-sm py-xs"
          style={{ borderColor: EDGES.soft }}
        >
          <span className="truncate font-cholo text-[16px] text-bone">{b.handle || `Player ${b.id.slice(0, 6)}`}</span>
          <button
            onClick={() => unblock(b.id)}
            className="min-h-[44px] rounded-sm border border-stucco3 px-sm font-data text-[12px] uppercase tracking-[0.2em] text-marker hover:text-bone md:min-h-[36px]"
          >
            unblock
          </button>
        </li>
      ))}
    </ul>
  );
}
